import { lazy, ReactNode, Suspense, useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as dayjs from 'dayjs';
import { ConfigProvider } from 'antd';
import enUS from 'antd/es/locale/en_US';
import ruRU from 'antd/es/locale/ru_RU';

import 'dayjs/locale/ru';
import { useAppDispatch, useAppSelector, useNotifier, usePermissions } from 'hooks';
import { Path } from 'enums';
import { getCurrentUserPermissionsLoaded, getLanguage, getUser } from 'store';
import { fetchCurrentUser, fetchCurrentUserSecurity } from 'services';
import { isUserRelevant } from 'helpers';

import styles from './App.module.scss';
import { Sidebar } from '../Sidebar';
import { PermissionsPage, RulesPage, SettingsPage, TaxesPage } from '../SettingsPage';
import { NotFoundPage } from '../NotFoundPage';
import { AccessDeniedPage } from '../AccessDeniedPage';
import { MyCompensationsPage } from '../MyCompensationsPage';
import { MyCompensationPage, CompensationRequestPage } from '../CompensationPage';
import { GoogleAnalyticsInitializer } from '../GoogleAnalytics/GoogleAnalyticsInitializer';
import { config } from '../../config/theme.config';
import { Spinner } from '../common/components/Spinner/Spinner';
import { AlertBanner } from '../AlertBanner/AlertBanner';
import { News } from '../News/News';

interface AppRoutes {
  path: Path | string;
  component: ReactNode;
  canActivate?: boolean;
  children?: AppRoutes[];
}

const ReportsPage = lazy(() => import('../ReportsPage/ReportsPage'));
const RequestsPage = lazy(() => import('../RequestsPage/RequestsPage'));
const ArchivePage = lazy(() => import('../ArchivePage/ArchivePage'));

export const App = () => {
  const dispatch = useAppDispatch();
  const language = useAppSelector(getLanguage);
  const isPermissionsLoaded = useAppSelector(getCurrentUserPermissionsLoaded);
  const { i18n } = useTranslation();
  const permissions = usePermissions();
  const [antLocale, setAntLocale] = useState(language === 'en' ? enUS : ruRU);
  const currentUser = useAppSelector(getUser);
  const isRelevantUser = isUserRelevant(currentUser || undefined);

  const routes = useMemo<AppRoutes[]>(
    () => [
      {
        path: Path.Home,
        component: <MyCompensationsPage />,
      },
      {
        path: `${Path.Home}/:id`,
        component: <MyCompensationPage />,
      },
      {
        path: Path.Requests,
        component: <RequestsPage />,
        canActivate: permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission,
      },
      {
        path: `${Path.Requests}/:id`,
        component: <CompensationRequestPage />,
        canActivate: permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission,
      },
      {
        path: Path.Archive,
        component: <ArchivePage />,
        canActivate: permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission,
      },
      {
        path: `${Path.Archive}/:id`,
        component: <CompensationRequestPage />,
        canActivate: permissions.hasManageCompensationsPermission || permissions.hasAdministratorPermission,
      },
      {
        path: Path.Reports,
        component: <ReportsPage />,
        canActivate: permissions.hasAdministratorPermission,
      },
      {
        path: Path.News,
        component: <News />,
      },
      {
        path: Path.Settings,
        component: <SettingsPage />,
        canActivate: permissions.hasAdministratorPermission,
        children: [
          {
            path: Path.Permissions,
            component: <PermissionsPage />,
          },
          {
            path: Path.Settlements,
            component: <RulesPage />,
          },
          {
            path: Path.Taxes,
            component: <TaxesPage />,
          },
          {
            path: '',
            component: <Navigate to={Path.Permissions} replace />,
          },
        ],
      },
      {
        path: Path.NotFound,
        component: <NotFoundPage />,
      },
      {
        path: '',
        component: <Navigate to={Path.Home} replace />,
      },
      {
        path: '*',
        component: <Navigate to={Path.NotFound} replace />,
      },
    ],
    [permissions],
  );

  useNotifier();

  useEffect(() => {
    i18n.changeLanguage(language);
    dayjs.locale(language);
    setAntLocale(language === 'en' ? enUS : ruRU);
  }, [i18n, language]);

  useEffect(() => {
    dispatch(fetchCurrentUser());
    dispatch(fetchCurrentUserSecurity());
  }, [dispatch]);

  const renderRoutes = (routeElements: AppRoutes[]) =>
    routeElements.map(
      ({ path, children, component, canActivate }) =>
        (canActivate === undefined || canActivate) && (
          <Route key={path} path={path} element={component}>
            {children && renderRoutes(children)}
          </Route>
        ),
    );

  if (!isPermissionsLoaded) {
    return <Spinner />;
  }

  return (
    <BrowserRouter>
      {isRelevantUser && (
        <div className={styles.app}>
          <Sidebar />

          <ConfigProvider locale={antLocale} theme={config}>
            <main className={styles.main}>
              <Suspense>
                <AlertBanner />
                <Routes>{renderRoutes(routes)}</Routes>
                <GoogleAnalyticsInitializer />
              </Suspense>
            </main>
          </ConfigProvider>
        </div>
      )}
      {isRelevantUser === false && <AccessDeniedPage />}
    </BrowserRouter>
  );
};
